import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
// Import the Dayjs adapter instead of DateFns
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  Alert,
  alpha,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
// Import dayjs for date handling
import dayjs from "dayjs";
import EqualizerIcon from "@mui/icons-material/Equalizer";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";

// Import visualization components
import DeliveryHeatmapMap from "./DeliveryHeatmapMap";
import MicroMarketAnalysis from "./MicroMarketAnalysis";
import DeliveryAnalyticsService from "../../../../api/DeliveryAnalyticsService";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
};

// Map tab index to endpoint
const tabToEndpoint = {
  0: "micro-market-analysis",
  1: "delivery-heatmap",
};

// Map tab index to data key
const tabToDataKey = {
  0: "microMarketData",
  1: "heatmapData",
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export default function DeliveryAnalyticsDashboard() {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [dateRange, setDateRange] = useState({
    startDate: dayjs().subtract(7, "day"),
    endDate: dayjs(),
  });
  const [daysFilter, setDaysFilter] = useState(7);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);
  const [analytics, setAnalytics] = useState({
    heatmapData: null,
    microMarketData: null,
  });
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const menuOpen = Boolean(menuAnchorEl);

  const handleMenuClick = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleDaysFilterChange = (event) => {
    const days = parseInt(event.target.value);
    setDaysFilter(days);
    setDateRange({
      startDate: dayjs().subtract(days, "day"),
      endDate: dayjs(),
    });
  };

  const handleDateChange = (date, type) => {
    if (date && date.isValid()) {
      setDateRange((prev) => ({
        ...prev,
        [type]: date,
      }));
    }
  };

  const handleErrorClose = () => {
    setShowError(false);
  };

  const loadTabData = useCallback(async () => {
    setLoading(true);
    setError(null);

    const params = {
      days: daysFilter,
      end_date: dateRange.endDate.format("YYYY-MM-DD"),
    };

    try {
      const endpoint = tabToEndpoint[tabValue];
      const dataKey = tabToDataKey[tabValue];

      let data;
      switch (endpoint) {
        case "delivery-heatmap":
          data = await DeliveryAnalyticsService.getDeliveryHeatmap(params);
          break;
        case "micro-market-analysis":
          data = await DeliveryAnalyticsService.getMicroMarketAnalysis(params);
          break;
        default:
          throw new Error(`Unsupported endpoint: ${endpoint}`);
      }

      setAnalytics((prev) => ({
        ...prev,
        [dataKey]: data.data, // Extract data from response
      }));
    } catch (error) {
      console.error("Error loading data:", error);
      setError(error.message || "Failed to load data");
      setShowError(true);
    } finally {
      setLoading(false);
    }
  }, [tabValue, daysFilter, dateRange.endDate]);

  useEffect(() => {
    loadTabData();
  }, [loadTabData]);

  const handleExportCSV = async () => {
    try {
      const params = {
        days: daysFilter,
        end_date: dateRange.endDate.format("YYYY-MM-DD"),
      };

      const endpoint = tabToEndpoint[tabValue];
      const response = await DeliveryAnalyticsService.exportAnalyticsToCSV(
        endpoint,
        params
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${endpoint}-${dayjs().format("YYYY-MM-DD")}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error exporting CSV:", error);
      setError("Failed to export CSV");
      setShowError(true);
    }
  };

  const handleGeneratePDF = async () => {
    try {
      const params = {
        days: daysFilter,
        end_date: dateRange.endDate.format("YYYY-MM-DD"),
      };

      const endpoint = tabToEndpoint[tabValue];
      const response = await DeliveryAnalyticsService.generateAnalyticsPDF(
        endpoint,
        params
      );

      // Create download link
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${endpoint}-report-${dayjs().format("YYYY-MM-DD")}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error generating PDF:", error);
      setError("Failed to generate PDF report");
      setShowError(true);
    }
  };

  const handleSaveDashboard = async () => {
    try {
      const dashboardConfig = {
        name: `Dashboard ${dayjs().format("YYYY-MM-DD HH:mm")}`,
        tab_index: tabValue,
        days_filter: daysFilter,
        end_date: dateRange.endDate.format("YYYY-MM-DD"),
      };

      await DeliveryAnalyticsService.saveCustomDashboard(dashboardConfig);
      handleMenuClose();
    } catch (error) {
      console.error("Error saving dashboard:", error);
      setError("Failed to save dashboard configuration");
      setShowError(true);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ p: 2 }}>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <Box
          sx={{
            mb: 4,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <EqualizerIcon sx={{ mr: 1, fontSize: 35 }} />
                Análise Avançada de Entregas
              </Box>
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Visualização detalhada do desempenho operacional das entregas no
              período selecionado
            </Typography>
          </Box>

          <Box>
            <Tooltip title="Mais opções">
              <IconButton
                onClick={handleMenuClick}
                size="large"
                aria-controls={menuOpen ? "dashboard-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={menuOpen ? "true" : undefined}
              >
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
            <Menu
              id="dashboard-menu"
              anchorEl={menuAnchorEl}
              open={menuOpen}
              onClose={handleMenuClose}
              MenuListProps={{
                "aria-labelledby": "dashboard-menu-button",
              }}
            >
              <MenuItem onClick={handleExportCSV}>
                <ListItemIcon>
                  <DownloadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Exportar CSV</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleGeneratePDF}>
                <ListItemIcon>
                  <PictureAsPdfIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Gerar Relatório PDF</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleSaveDashboard}>
                <ListItemIcon>
                  <SaveIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Salvar Dashboard</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 4,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
          component={motion.div}
          variants={itemVariants}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4} md={2}>
              <FormControl fullWidth>
                <InputLabel id="days-filter-label">Período</InputLabel>
                <Select
                  labelId="days-filter-label"
                  id="days-filter"
                  value={daysFilter}
                  label="Período"
                  onChange={handleDaysFilterChange}
                >
                  <MenuItem value={7}>Últimos 7 dias</MenuItem>
                  <MenuItem value={15}>Últimos 15 dias</MenuItem>
                  <MenuItem value={30}>Últimos 30 dias</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={8} md={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Stack direction="row" spacing={2}>
                  <DatePicker
                    label="Data Inicial"
                    value={dateRange.startDate}
                    onChange={(date) => handleDateChange(date, "startDate")}
                    disabled
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                  <DatePicker
                    label="Data Final"
                    value={dateRange.endDate}
                    onChange={(date) => handleDateChange(date, "endDate")}
                    maxDate={dayjs()}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Stack>
              </LocalizationProvider>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{ textAlign: { xs: "left", md: "right" } }}
            >
              <Button
                variant="contained"
                onClick={loadTabData}
                startIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <RefreshIcon />
                  )
                }
                disabled={loading}
              >
                {loading ? "Carregando..." : "Atualizar Dados"}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
          component={motion.div}
          variants={itemVariants}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              px: 2,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <Tab label="Análise por Bairro" />
            <Tab label="Mapa de Calor" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <MicroMarketAnalysis
              data={analytics.microMarketData}
              loading={loading}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <DeliveryHeatmapMap
              data={analytics.heatmapData}
              loading={loading}
            />
          </TabPanel>
        </Paper>
      </motion.div>

      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={handleErrorClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleErrorClose}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
}
